import React, { Suspense, useMemo } from 'react';

import { Routes } from 'react-router-dom';

import { SentryRoute as Route } from '@vk-hr-tek/core/monitoring/SentryRoute';

import { ModulesNames, modules } from '@app/modules';
import { useIsWidget } from '@app/hooks';

import { RequireAuth, PageLoading } from './layout';

const Auth = React.lazy(async () => import('./auth'));
const Candidates = React.lazy(async () => import('./candidates'));
const Employees = React.lazy(async () => import('./employees'));
const Events = React.lazy(async () => import('./events'));
const Organization = React.lazy(async () => import('./organization'));
const Settings = React.lazy(async () => import('./settings'));
const Vacations = React.lazy(async () => import('./vacations'));
const Policy = React.lazy(async () => import('./policy'));
const Profile = React.lazy(async () => import('./user/Profile'));
const CreateCertificate = React.lazy(
  async () => import('./user/CreateCertificate'),
);
const Payslips = React.lazy(async () => import('./payslips'));
const Competencies = React.lazy(async () => import('./competencies'));
const BellNotifications = React.lazy(
  async () => import('./bell-notifications'),
);
const Dashboard = React.lazy(async () => import('./dashboard'));
const Absences = React.lazy(async () => import('./absences'));
const Personal = React.lazy(async () => import('./personal'));
const SideMenu = React.lazy(async () => import('./layout/SideMenu'));
const Header = React.lazy(async () => import('./layout/Header'));
const GeneralNews = React.lazy(async () => import('./general-news'));
const News = React.lazy(async () => import('./news'));
const Quiz = React.lazy(async () => import('./quiz'));
const Review = React.lazy(async () => import('./review'));
const SocialCategories = React.lazy(async () => import('./social-categories'));
const SocialAccess = React.lazy(async () => import('./social-access'));

const resources: {
  module: ModulesNames;
  Component: React.ComponentType;
}[] = [
  {
    module: 'events',
    Component: Events,
  },
  {
    module: 'candidates',
    Component: Candidates,
  },
  {
    module: 'employees',
    Component: Employees,
  },
  {
    module: 'policy',
    Component: Policy,
  },
  {
    module: 'payslips',
    Component: Payslips,
  },
  {
    module: 'competencies',
    Component: Competencies,
  },
  {
    module: 'bell_notifications',
    Component: BellNotifications,
  },
  {
    module: 'settings',
    Component: Settings,
  },
  {
    module: 'vacations',
    Component: Vacations,
  },
  {
    module: 'absences',
    Component: Absences,
  },
  {
    module: 'sideMenu',
    Component: SideMenu,
  },
  {
    module: 'header',
    Component: Header,
  },
  {
    module: 'social_news',
    Component: News,
  },
  {
    module: 'news',
    Component: GeneralNews,
  },
  {
    module: 'review',
    Component: Review,
  },
  {
    module: 'social_categories',
    Component: SocialCategories,
  },
  {
    module: 'social_access',
    Component: SocialAccess,
  },
  { module: 'quiz', Component: Quiz },
];

function App({ page = 'all' }: { page?: string }) {
  const isWidget = useIsWidget();

  if (isWidget) {
    resources.push(
      {
        module: 'structure',
        Component: Organization,
      },
      {
        module: 'personal',
        Component: Personal,
      },
    );
  }

  const routes = useMemo(() => {
    return page === 'all'
      ? resources.map((resource) => ({
          ...resource,
          path: modules[resource.module].basename,
          resource: modules[resource.module].resource,
        }))
      : resources
          .filter(({ module }) => {
            if (page === 'competencies') {
              return module === 'events' || module === page;
            }

            if (page === 'absences') {
              return (
                module === 'events' || module === 'vacations' || module === page
              );
            }

            return module === page;
          })
          .map((resource, index, arr) => ({
            ...resource,
            path:
              arr.length > 1 && resource.module !== page
                ? `/${modules[resource.module].basename}`
                : '',
            resource: modules[resource.module].resource,
          }));
  }, [page]);

  if (page === 'all') {
    return (
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="auth/*" element={<Auth />} />
          <Route
            path="create-certificate/*"
            element={
              <RequireAuth resource="User" role="employee">
                <CreateCertificate />
              </RequireAuth>
            }
          />
          <Route
            path="profile/*"
            element={
              <RequireAuth resource="User" role="employee">
                <Profile />
              </RequireAuth>
            }
          />
          <Route
            path="news/*"
            element={
              <RequireAuth resource="News" role="general">
                <GeneralNews />
              </RequireAuth>
            }
          />
          <Route
            path="notifications/*"
            element={
              <RequireAuth resource="User" role="employee">
                <BellNotifications />
              </RequireAuth>
            }
          />
          <Route
            path="organization/*"
            element={
              <RequireAuth resource="Organization" role="general">
                <Organization />
              </RequireAuth>
            }
          />
          <Route
            path="personal/*"
            element={
              <RequireAuth resource="Personal" role="employee">
                <Personal />
              </RequireAuth>
            }
          />
          <Route
            path="company/*"
            element={
              <RequireAuth resource="Events" role="company">
                <Routes>
                  {routes.map(({ path, resource, Component }) => {
                    return (
                      <Route
                        path={`${path}/*`}
                        key={path}
                        element={
                          <RequireAuth resource={resource} role="company">
                            <Component />
                          </RequireAuth>
                        }
                      />
                    );
                  })}
                </Routes>
              </RequireAuth>
            }
          />
          <Route
            path="employee/*"
            element={
              <RequireAuth resource="Events" role="employee">
                <Routes>
                  {routes.map(({ path, resource, Component }) => (
                    <Route
                      path={`${path}/*`}
                      key={path}
                      element={
                        <RequireAuth resource={resource} role="employee">
                          <Component />
                        </RequireAuth>
                      }
                    />
                  ))}
                </Routes>
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={
              <RequireAuth resource="Events" role="employee">
                <Dashboard />
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="auth/*" element={<Auth />} />
        <Route
          path="create-certificate/*"
          element={
            <RequireAuth resource="User" role="employee">
              <CreateCertificate />
            </RequireAuth>
          }
        />
        <Route
          path="profile/*"
          element={
            <RequireAuth resource="User" role="employee">
              <Profile />
            </RequireAuth>
          }
        />
        {routes.map(({ path, resource, Component }) => (
          <Route
            path={`${path}/*`}
            key={path}
            element={
              <RequireAuth resource={resource}>
                <Component />
              </RequireAuth>
            }
          />
        ))}
        <Route
          path="*"
          element={
            <RequireAuth resource="Events" role="employee">
              <Dashboard />
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
